import {
  AUTH_TYPE,
  AuthTypeId,
  DEFAULT_AUTH_TYPE,
  HttpMethodId,
  ServiceProvider,
  StageConfig,
  StageKey,
  StatusKey,
} from "../../../utils/integrationConfigData";
import {
  AUTH_KEY,
  CREDENTIAL_KEY,
  DETAIL_KEY,
  isTestSuccess,
  KEY_TYPE,
  REQUEST_BODY_KEY,
  REQUEST_URL_KEY,
} from "../../../constants/integrationKeys";
import { itemsFromPairs } from "../../../utils/keyValueItems";
import { buildTestResultDisplayMessage } from "../../../utils/testResultMessage";

export type ProviderStatusValue = "Active" | "Inactive" | "Out of Scope";

export interface ProviderFormValues {
  name: string;
  code: string;
  description: string;
  status: ProviderStatusValue;
  subscriptionKey: string;
}

export interface ProviderListItemApi {
  id: number;
  providerName: string;
  providerCode: string;
  status: string;
  cptCodeCount: number;
  crdStatus: StatusKey;
  dtrStatus: StatusKey;
  pasStatus: StatusKey;
}

export interface ProviderListResponseApi {
  providers: ProviderListItemApi[];
  healthyCount: number;
  unhealthyCount: number;
}

export interface ProviderDetailApi extends ProviderListItemApi {
  description?: string;
  subscriptionKey?: string;
}

export interface PAConfigurationKeyValueApi {
  id?: number;
  keyType: string;
  key: string;
  value: string;
}

export interface PAConfigurationTestResultApi {
  id: number;
  testStatus: string;
  httpStatusCode?: number;
  responseMessage?: string;
  responseTimeMs?: number;
  testedBy?: string;
  testedAt?: string;
}

export interface PAConfigurationApi {
  id: number;
  providerId: number;
  integrationType: string;
  endpointUrl: string;
  authTypeId: number;
  authTypeName?: string;
  configurationTypeId?: number | null;
  configurationType?: string | null;
  configurationTypeDisplayName?: string | null;
  status: StatusKey;
  isDeleted?: boolean;
  credentials: PAConfigurationKeyValueApi[];
  details: PAConfigurationKeyValueApi[];
  testResult?: PAConfigurationTestResultApi | null;
}

export interface CptCodeApi {
  id: number;
  providerId: number;
  cptCode: string;
  codeType: string;
}

export interface CptCodeSearchResultApi {
  id: number;
  providerId: number;
  cptCode: string;
  codeType: string;
  providerName?: string | null;
  providerCode?: string | null;
}

export interface PaginatedCptCodesApi {
  items: CptCodeApi[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
}

export interface CptCodeOwnershipConflictApi {
  cptCode: string;
  ownerProviderId: number;
  ownerProviderCode: string;
  ownerProviderName: string;
}

export interface BulkAddCptCodesResponseApi {
  added: number;
  duplicates: string[];
  conflicts: CptCodeOwnershipConflictApi[];
  errors: string[];
  resultFileName?: string | null;
  resultFileContent?: string | null;
}

export interface ImportCptCodesResponseApi extends BulkAddCptCodesResponseApi {}

export interface AuthTypeApi {
  id: number;
  type: string;
}

export interface ConfigurationTypeApi {
  id: number;
  integrationType: string;
  configurationType: string;
  displayName: string;
}

export interface SavePAConfigurationRequest {
  providerId: number;
  integrationType: string;
  endpointUrl: string;
  authTypeId: number;
  configurationTypeId?: number | null;
  credentials: PAConfigurationKeyValueApi[];
  details: PAConfigurationKeyValueApi[];
  createdBy?: string;
  updatedBy?: string;
}

const emptyStage = (): StageConfig => ({
  status: "notSet",
  description: "",
});

export const mapProviderListItemToServiceProvider = (
  dto: ProviderListItemApi,
  description?: string,
): ServiceProvider => ({
  id: String(dto.id),
  shortName: dto.providerCode,
  name: dto.providerName,
  path: `Providers → ${dto.providerCode}`,
  status: dto.status,
  note: description,
  crd: dto.crdStatus,
  dtr: dto.dtrStatus,
  pas: dto.pasStatus,
  cptCodes: dto.cptCodeCount,
  assignedCodes: [],
  stages: {
    crd: { ...emptyStage(), status: dto.crdStatus },
    dtr: { ...emptyStage(), status: dto.dtrStatus },
    pas: { ...emptyStage(), status: dto.pasStatus },
  },
});

export const mapProviderDetailToFormValues = (
  dto: ProviderDetailApi,
): ProviderFormValues => ({
  name: dto.providerName,
  code: dto.providerCode,
  description: dto.description || "",
  status: normalizeProviderStatus(dto.status),
  subscriptionKey: dto.subscriptionKey || "",
});

const normalizeProviderStatus = (status: string): ProviderFormValues["status"] => {
  const normalized = status.trim().toLowerCase();
  if (normalized.includes("inactive")) return "Inactive";
  if (normalized.includes("out of scope")) return "Out of Scope";
  return "Active";
};

const getDetail = (api: PAConfigurationApi, key: string) =>
  api.details?.find((d) => d.key.toLowerCase() === key.toLowerCase())?.value;

const getByKeyType = (
  api: PAConfigurationApi,
  keyType: string,
  key: string,
) =>
  api.credentials?.find(
    (c) =>
      c.keyType.toLowerCase() === keyType.toLowerCase() &&
      c.key.toLowerCase() === key.toLowerCase(),
  )?.value;

const getCredentialAny = (api: PAConfigurationApi, key: string) =>
  api.credentials?.find((c) => c.key.toLowerCase() === key.toLowerCase())?.value;

const listByKeyType = (api: PAConfigurationApi, keyType: string) =>
  (api.credentials || [])
    .filter((c) => c.keyType.toLowerCase() === keyType.toLowerCase())
    .map((c) => ({ key: c.key, value: c.value }));

const listDetailsByKeyType = (api: PAConfigurationApi, keyType: string) =>
  (api.details || [])
    .filter((d) => d.keyType.toLowerCase() === keyType.toLowerCase())
    .map((d) => ({ key: d.key, value: d.value }));

const getDetailByKeyType = (
  api: PAConfigurationApi,
  keyType: string,
  key: string,
) =>
  api.details?.find(
    (d) =>
      d.keyType.toLowerCase() === keyType.toLowerCase() &&
      d.key.toLowerCase() === key.toLowerCase(),
  )?.value;

const normalizeHttpMethod = (value?: string): HttpMethodId =>
  value?.trim().toUpperCase() === "POST" ? "POST" : "GET";

export const mapIntegrationToStageConfig = (
  api: PAConfigurationApi,
): StageConfig => {
  const test = api.testResult;
  const testOk = isTestSuccess(test?.testStatus) || api.status === "verified";

  let requestUrls = listByKeyType(api, KEY_TYPE.RequestUrl);
  // Connection vs credentials headers stay separate (no merge).
  let headers = listDetailsByKeyType(api, KEY_TYPE.Header);
  let credentialHeaders = listByKeyType(api, KEY_TYPE.Header);
  let queryParams = listByKeyType(api, KEY_TYPE.QueryParam);
  let requestBody = listByKeyType(api, KEY_TYPE.RequestBody);

  const legacyToken =
    getByKeyType(api, KEY_TYPE.RequestUrl, REQUEST_URL_KEY.TokenUrl) ||
    getDetail(api, DETAIL_KEY.TokenUrl);
  if (legacyToken && !requestUrls.some((r) => r.key.toLowerCase() === "tokenurl")) {
    requestUrls = [...requestUrls, { key: REQUEST_URL_KEY.TokenUrl, value: legacyToken }];
  }

  const legacyClientId =
    getByKeyType(api, KEY_TYPE.RequestBody, REQUEST_BODY_KEY.ClientId) ||
    getDetail(api, DETAIL_KEY.ClientId);
  const legacySecret =
    getByKeyType(api, KEY_TYPE.RequestBody, REQUEST_BODY_KEY.ClientSecret) ||
    getCredentialAny(api, CREDENTIAL_KEY.ClientSecret);
  const legacyScope =
    getByKeyType(api, KEY_TYPE.RequestBody, REQUEST_BODY_KEY.Scope) ||
    getDetail(api, DETAIL_KEY.Scope);

  if (legacyClientId && !requestBody.some((r) => r.key.toLowerCase() === "clientid")) {
    requestBody = [...requestBody, { key: REQUEST_BODY_KEY.ClientId, value: legacyClientId }];
  }
  if (legacySecret && !requestBody.some((r) => r.key.toLowerCase() === "clientsecret")) {
    requestBody = [
      ...requestBody,
      { key: REQUEST_BODY_KEY.ClientSecret, value: legacySecret },
    ];
  }
  if (legacyScope && !requestBody.some((r) => r.key.toLowerCase() === "scope")) {
    requestBody = [...requestBody, { key: REQUEST_BODY_KEY.Scope, value: legacyScope }];
  }

  const legacyHeaderName = getDetail(api, DETAIL_KEY.HeaderName);
  const fixedAuthKeys = new Set(
    Object.values(AUTH_KEY).map((k) => k.toLowerCase()),
  );
  // Current save shape: keyType Authentication, key = header name, value = secret.
  const apiKeyAuthCredential = (api.credentials || []).find(
    (c) =>
      c.keyType.toLowerCase() === KEY_TYPE.Authentication.toLowerCase() &&
      Boolean(c.key?.trim()) &&
      !fixedAuthKeys.has(c.key.toLowerCase()),
  );
  const apiKeyCredential = (api.credentials || []).find(
    (c) => c.keyType.toLowerCase() === KEY_TYPE.ApiKey.toLowerCase(),
  );
  const legacyApiKey =
    getByKeyType(api, KEY_TYPE.Authentication, AUTH_KEY.ApiKey) ||
    getCredentialAny(api, CREDENTIAL_KEY.ApiKey);
  if (
    legacyHeaderName &&
    legacyApiKey &&
    !apiKeyAuthCredential &&
    !apiKeyCredential &&
    !headers.some((h) => h.key.toLowerCase() === legacyHeaderName.toLowerCase())
  ) {
    headers = [...headers, { key: legacyHeaderName, value: legacyApiKey }];
  }

  const bearerToken =
    getByKeyType(api, KEY_TYPE.Authentication, AUTH_KEY.BearerToken) ||
    getCredentialAny(api, CREDENTIAL_KEY.BearerToken);
  const username =
    getByKeyType(api, KEY_TYPE.Authentication, AUTH_KEY.Username) ||
    getDetail(api, DETAIL_KEY.Username);
  const password =
    getByKeyType(api, KEY_TYPE.Authentication, AUTH_KEY.Password) ||
    getCredentialAny(api, CREDENTIAL_KEY.Password);

  return {
    status: api.status || "notSet",
    endpoint: api.endpointUrl,
    authType: mapAuthTypeNameToId(api.authTypeName),
    tokenUrl: legacyToken,
    clientId: legacyClientId,
    scope: legacyScope,
    headerName:
      apiKeyAuthCredential?.key ||
      apiKeyCredential?.key ||
      legacyHeaderName ||
      headers[0]?.key,
    username,
    questionSource:
      api.configurationType || getDetail(api, DETAIL_KEY.QuestionSource),
    crdEndpointType:
      api.configurationType || getDetail(api, DETAIL_KEY.CrdEndpointType),
    requestFormat:
      getDetailByKeyType(api, KEY_TYPE.PASformats, DETAIL_KEY.RequestFormat) ||
      getDetail(api, DETAIL_KEY.RequestFormat),
    responseFormat:
      getDetailByKeyType(api, KEY_TYPE.PASformats, DETAIL_KEY.ResponseFormat) ||
      getDetail(api, DETAIL_KEY.ResponseFormat),
    samplePayload: getDetail(api, DETAIL_KEY.SamplePayload),
    httpMethod: normalizeHttpMethod(
      getByKeyType(api, KEY_TYPE.Method, DETAIL_KEY.Method) ||
        getCredentialAny(api, DETAIL_KEY.Method) ||
        getDetail(api, DETAIL_KEY.Method),
    ),
    clientSecret: legacySecret,
    bearerToken,
    password,
    apiKey:
      apiKeyAuthCredential?.value ||
      apiKeyCredential?.value ||
      legacyApiKey,
    requestUrls: itemsFromPairs(requestUrls),
    headers: itemsFromPairs(headers),
    credentialHeaders: itemsFromPairs(credentialHeaders),
    queryParams: itemsFromPairs(queryParams),
    requestBody: itemsFromPairs(requestBody),
    isDeleted: Boolean(api.isDeleted),
    testResult: test
      ? {
          status: testOk ? "success" : "error",
          message: formatConfiguredTestMessage(test),
        }
      : undefined,
  };
};

const formatConfiguredTestMessage = (test: PAConfigurationTestResultApi) =>
  buildTestResultDisplayMessage(test);

const mapAuthTypeNameToId = (name?: string): AuthTypeId => {
  const n = (name || "").toLowerCase();
  if (n.includes("bearer")) return AUTH_TYPE.BearerToken;
  if (n.includes("oauth") || n.includes("client"))
    return AUTH_TYPE.OAuth2ClientCredentials;
  if (n.includes("basic")) return AUTH_TYPE.Basic;
  if (n.includes("api") || n.includes("key")) return AUTH_TYPE.ApiKeyHeader;
  return DEFAULT_AUTH_TYPE;
};

export const mapAuthTypeIdToApiId = (
  authType: AuthTypeId,
  authTypes: AuthTypeApi[],
): number | undefined => {
  const match = authTypes.find((a) => {
    const t = a.type.toLowerCase();
    switch (authType) {
      case AUTH_TYPE.BearerToken:
        return t.includes("bearer");
      case AUTH_TYPE.OAuth2ClientCredentials:
        return t.includes("oauth") || t.includes("client");
      case AUTH_TYPE.Basic:
        return t.includes("basic");
      case AUTH_TYPE.ApiKeyHeader:
        return t.includes("api") || t.includes("key");
      default:
        return false;
    }
  });
  return match?.id;
};

export type IntegrationTypeParam = StageKey;
